import { useMemo } from 'react'
import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'
import type { SegmentQueryField } from '@pushly/models/lib/interfaces/segment-query-field'
import type { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'

export interface SegmentQueryFieldFilter {
    channels?: DeliveryChannel[]
    domainIds?: number[]
}

/**
 * Narrows the server-supplied catalog to the fields that apply to the current
 * selection, then unwraps each entry to the generic field definition the
 * builder consumes.
 *
 * An empty or absent selection is treated as "not yet chosen" and filters
 * nothing — collapsing the catalog to zero fields before the user has picked
 * channels would leave them nothing to build with.
 *
 * Server order is preserved: the registry emits fields in the order the
 * property picker is expected to group them.
 */
export function filterSegmentQueryFields(
    fields: SegmentQueryField[],
    { channels, domainIds }: SegmentQueryFieldFilter,
): PQueryBuilderField[] {
    const hasChannelSelection = !!channels?.length
    const hasDomainSelection = !!domainIds?.length

    return fields
        .filter((entry) => {
            if (hasChannelSelection && entry.channels?.length) {
                if (!entry.channels.some((channel) => channels!.includes(channel))) return false
            }

            if (hasDomainSelection && entry.domainIds?.length) {
                if (!entry.domainIds.some((id) => domainIds!.includes(id))) return false
            }

            return true
        })
        .map((entry) => entry.field)
}

export function useSegmentQueryFields(
    fields: SegmentQueryField[],
    filter: SegmentQueryFieldFilter,
): PQueryBuilderField[] {
    const { channels, domainIds } = filter

    return useMemo(
        () => filterSegmentQueryFields(fields, { channels, domainIds }),
        // The selections are joined so a new array carrying identical contents does
        // not re-derive the catalog on every parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fields, channels?.join(','), domainIds?.join(',')],
    )
}
